/**
 * Assignment presentation (issue #71, ADR 0002) — labels shared by the
 * instructor and student workspaces, and the one place an assignment's
 * learner-facing state is derived from its attempts.
 *
 * The server derives `state` per ATTEMPT. A learner reads a whole assignment,
 * so `assigned` is added here for published work with no attempt yet — it is a
 * presentation state, not one of the ADR's four.
 */
import type { SemesterStatus } from "@/lib/api/academicStructure";
import type {
  Assignment,
  AssignmentStatus,
  ContentKind,
  ExpectedDeliverable,
  Submission,
  SubmissionState,
} from "@/lib/api/assignments";
import { isHistoricalTerm } from "@/lib/term-state";

/** No `default` on purpose: that variant renders `bg-primary`, and academic
 *  state must not wear the tenant accent. See badge-status.test.ts. */
type BadgeVariant = "secondary" | "outline" | "destructive" | "success" | "warning" | "info";

export type LearnerState = "assigned" | SubmissionState;

export const ASSIGNMENT_STATUS_LABEL: Record<AssignmentStatus, string> = {
  draft: "Draft",
  published: "Published",
  closed: "Closed",
};

export const ASSIGNMENT_STATUS_VARIANT: Record<AssignmentStatus, BadgeVariant> = {
  draft: "secondary",
  published: "success",
  closed: "outline",
};

export const LEARNER_STATE_LABEL: Record<LearnerState, string> = {
  assigned: "Not submitted",
  submitted: "Submitted",
  returned: "Returned for revision",
  resubmitted: "Resubmitted",
  graded: "Graded",
};

export const LEARNER_STATE_VARIANT: Record<LearnerState, BadgeVariant> = {
  assigned: "outline",
  submitted: "secondary",
  returned: "destructive",
  resubmitted: "secondary",
  graded: "success",
};

export const EXPECTED_DELIVERABLE_LABEL: Record<ExpectedDeliverable, string> = {
  text: "Structured writing",
  link: "Link",
  artifact: "Private file",
  none: "Nothing to hand in",
};

export const SUBMISSION_KIND_LABEL: Record<ContentKind, string> = {
  quiz: "Quiz answers",
  link: "Link",
  text: "Written response",
  artifact: "File",
  attested: "Marked by the learner",
  recorded: "Recorded",
};

/** Newest first. The attempt number cannot order them: a Missing mark has none, and a
 *  hand-in after it is the learner's attempt 1. The time each was made does. */
export function newestFirst(a: Submission, b: Submission): number {
  return Date.parse(b.submitted_at) - Date.parse(a.submitted_at) || (b.attempt_number ?? 0) - (a.attempt_number ?? 0);
}

/** The newest attempt; the list arrives ordered but need not stay so. */
export function latestAttempt(attempts: Submission[]): Submission | null {
  return attempts.reduce<Submission | null>(
    (latest, attempt) => (!latest || newestFirst(attempt, latest) < 0 ? attempt : latest),
    null,
  );
}

export function learnerStateOf(attempts: Submission[]): LearnerState {
  return latestAttempt(attempts)?.state ?? "assigned";
}

/**
 * Whether the learner may make an attempt now. A return is the only thing that
 * permits another one (ADR 0002), and a closed term permits none at all (issue
 * #95 — `create_submission` answers "<term> is closed, so work cannot be
 * submitted to its assignments"), so this mirrors what the database will accept
 * rather than showing a form the write path then refuses.
 *
 * The term is a REQUIRED argument, not an optional one defaulting to open: a
 * caller that has not answered "which term is this course in?" fails to
 * compile, so the gate cannot be lost by a future call site that simply did not
 * know to pass it. The status itself is passed rather than a derived boolean,
 * so no caller can derive it differently from the term list and the catalog.
 */
export function mayAttempt(
  assignment: Assignment,
  attempts: Submission[],
  semesterStatus: SemesterStatus | null | undefined,
): boolean {
  if (assignment.status !== "published") return false;
  if (isHistoricalTerm(semesterStatus)) return false;
  const state = learnerStateOf(attempts);
  return state === "assigned" || state === "returned";
}

/**
 * The idempotency key for one intended attempt.
 *
 * Stable for unchanged content so a retry of a lost response returns the
 * recorded receipt, and different once the draft changes so an edit is not
 * refused as a different payload under a used key. The attempt ordinal keeps a
 * resubmission of identical content from resolving to the earlier attempt.
 */
export function attemptKey(assignmentId: string, attemptNumber: number, content: string): string {
  let hash = 5381;
  for (let i = 0; i < content.length; i += 1) {
    hash = (hash * 33 + content.charCodeAt(i)) | 0;
  }
  return `${assignmentId}:${attemptNumber}:${(hash >>> 0).toString(36)}`;
}

export function formatWhen(value: string | null, fallback = "—"): string {
  return value ? new Date(value).toLocaleString() : fallback;
}
